import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { Navbar } from '../../components/Navbar';
import { PaymentSettingsCard } from '../../components/PaymentSettingsCard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { ArrowLeft, Settings as SettingsIcon, Search, ShoppingCart, Truck, Activity, CreditCard, Cpu, Trash2, AlertTriangle, Database, RefreshCw, BellOff, Image as ImageIcon, Wand2 } from 'lucide-react';
import { ChunkedBatchAction } from '../../components/ChunkedBatchAction';
import { SentryTriagePanel } from '../../components/SentryTriagePanel';
import { TierConfigPanel } from '../../components/TierConfigPanel';
import { LaunchEmailsPanel } from '../../components/LaunchEmailsPanel';
import { ThemePollResults } from '../../components/ThemePollResults';
import { FlagHistoryPanel } from '../../components/FlagHistoryPanel';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const CATEGORY_META = {
  customer: { label: 'CUSTOMER', icon: ShoppingCart, color: '#39FF14' },
  driver: { label: 'DRIVER', icon: Truck, color: '#00F0FF' },
  dispatcher: { label: 'DISPATCHER', icon: Activity, color: '#FF9F0A' },
  payment: { label: 'PAYMENT', icon: CreditCard, color: '#FFD60A' },
  system: { label: 'SYSTEM', icon: Cpu, color: '#BF5AF2' },
};

const SUPER_ADMIN_FLAGS = new Set(['customer.modern_theme_toggle', 'customer.theme_feedback_poll']);

export function SettingsPage() {
  const { user } = useAuth();
  const { list, loaded, refetch } = useFeatureFlags();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [savingKey, setSavingKey] = useState(null);

  // Feb 2026 — when the health badge in the dispatcher header navigates here
  // with `#sentry-triage`, smooth-scroll to the panel so it's instantly
  // obvious why the operator landed on Settings (previously it just dropped
  // them at the top, which felt indistinguishable from the hamburger entry).
  useEffect(() => {
    if (!loaded) return;
    if (location.hash !== '#sentry-triage') return;
    const tries = [0, 150, 400, 800];
    const timers = tries.map((d) => setTimeout(() => {
      const el = document.getElementById('sentry-triage');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, d));
    return () => timers.forEach(clearTimeout);
  }, [location.hash, loaded]);

  const grouped = useMemo(() => {
    const filtered = list.filter(f => {
      if (!query.trim()) return true;
      const q = query.trim().toLowerCase();
      return (
        f.key.toLowerCase().includes(q) ||
        f.label.toLowerCase().includes(q) ||
        f.description.toLowerCase().includes(q)
      );
    });
    const groups = {};
    for (const f of filtered) {
      const cat = f.category || 'misc';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(f);
    }
    return groups;
  }, [list, query]);

  const totals = useMemo(() => ({
    on: list.filter(f => f.enabled).length,
    off: list.filter(f => !f.enabled).length,
    planned: list.filter(f => f.planned).length,
  }), [list]);

  const toggle = async (flag) => {
    setSavingKey(flag.key);
    try {
      await axios.patch(
        `${API_URL}/api/feature-flags/${flag.key}`,
        { enabled: !flag.enabled },
        { withCredentials: true }
      );
      toast.success(`${flag.label}: ${!flag.enabled ? 'ON' : 'OFF'}`);
      // SSE will push the change; do an immediate refetch as a safety net
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toggle failed');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#050003] dispatch-safe-top">
      <Navbar />
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <Link to="/dispatch" className="inline-flex items-center gap-2 text-[#39FF14] hover:text-[#4AF626] mb-4 font-mono text-sm">
          <ArrowLeft className="w-4 h-4" />
          BACK_TO_DISPATCH
        </Link>

        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-[#39FF14] font-mono text-2xl font-bold neon-text flex items-center gap-3">
              <SettingsIcon className="w-6 h-6" />
              {'>'} FEATURE_SETTINGS
            </h1>
            <p className="text-[#4B6B52] font-mono text-sm mt-1">
              // Toggle features for all users in real-time. {totals.on} ON · {totals.off} OFF · {totals.planned} planned.
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-lg mb-6">
          <Search className="w-4 h-4 text-[#4B6B52] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search flags by name, key, or description..."
            className="pl-9 bg-[#0C0612] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
            data-testid="flags-search"
          />
        </div>

        {/* Phase 5.5-Lite — Payment methods config (super-admin only) */}
        {user?.role === 'super_admin' && <LaunchEmailsPanel />}
        {user?.role === 'super_admin' && <ThemePollResults />}
        <FlagHistoryPanel />
        {user?.role === 'super_admin' && <PaymentSettingsCard />}

        {/* Ops Tools — super-admin only */}
        {user?.role === 'super_admin' && <SentryTriagePanel />}
        {user?.role === 'super_admin' && <TierConfigPanel />}
        {user?.role === 'super_admin' && <SweepReportPanel />}
        {user?.role === 'super_admin' && <OpsToolsPanel />}

        {!loaded ? (
          <p className="text-[#4B6B52] font-mono text-sm">Loading flags...</p>
        ) : list.length === 0 ? (
          <div className="text-center py-12 border border-[#2A0F3D] bg-[#0C0612]">
            <p className="text-[#4B6B52] font-mono">// NO FLAGS REGISTERED</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped).map(([cat, flags]) => {
              const meta = CATEGORY_META[cat] || { label: cat.toUpperCase(), icon: SettingsIcon, color: '#A3C7A8' };
              const Icon = meta.icon;
              return (
                <div key={cat} className="bg-[#0C0612] border border-[#2A0F3D]" data-testid={`flag-group-${cat}`}>
                  <div
                    className="flex items-center gap-3 px-5 py-3 border-b"
                    style={{ borderColor: meta.color, color: meta.color }}
                  >
                    <Icon className="w-5 h-5" />
                    <h2 className="font-mono text-sm font-bold uppercase tracking-wider">{meta.label}</h2>
                    <span className="ml-auto font-mono text-xs opacity-60">
                      {flags.filter(f => f.enabled).length}/{flags.length} ON
                    </span>
                  </div>
                  <div className="divide-y divide-[#2A0F3D]">
                    {flags.map(flag => (
                      <div
                        key={flag.key}
                        className="flex items-start justify-between gap-4 px-5 py-4 hover:bg-[#111A14] transition-colors"
                        data-testid={`flag-row-${flag.key}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`font-mono text-sm font-bold ${flag.enabled ? 'text-[#39FF14]' : 'text-[#A3C7A8]'}`}>
                              {flag.label}
                            </p>
                            {flag.planned && (
                              <span className="px-1.5 py-0.5 border border-[#FF9F0A] text-[#FF9F0A] font-mono text-[9px] uppercase">
                                planned
                              </span>
                            )}
                            <code className="text-[#4B6B52] font-mono text-[10px] truncate">
                              {flag.key}
                            </code>
                          </div>
                          <p className="text-[#6B8E73] font-mono text-xs mt-1">{flag.description}</p>
                          {flag.updated_by && flag.updated_by !== 'system_seed' && flag.updated_at && (
                            <p className="text-[#4B6B52] font-mono text-[10px] mt-1">
                              Last changed by {flag.updated_by} · {new Date(flag.updated_at).toLocaleString()}
                            </p>
                          )}
                          {SUPER_ADMIN_FLAGS.has(flag.key) && user?.role !== 'super_admin' && (
                            <p className="text-[#FF9F0A] font-mono text-[10px] mt-1" data-testid={`flag-locked-${flag.key}`}>Super Admin only</p>
                          )}
                        </div>
                        <div className="shrink-0">
                          <Switch
                            checked={flag.enabled}
                            disabled={savingKey === flag.key || (SUPER_ADMIN_FLAGS.has(flag.key) && user?.role !== 'super_admin')}
                            onCheckedChange={() => toggle(flag)}
                            data-testid={`flag-toggle-${flag.key}`}
                            className="data-[state=checked]:bg-[#39FF14] data-[state=unchecked]:bg-[#2A0F3D]"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-[#4B6B52] font-mono text-xs mt-8 text-center">
          // Changes propagate live to all logged-in users via SSE
        </p>
        {!user && null}
      </div>
    </div>
  );
}


// ============================================================================
// ============================================================================
// SweepReportPanel — super-admin DB health snapshot (Phase 7.0-lite, P0-a)
//   * Pulls GET /api/admin/sweep/report
//   * READ-ONLY — never mutates. Cleanup happens via OpsToolsPanel below.
// ============================================================================

function SweepReportPanel() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/sweep/report`,
        { withCredentials: true }
      );
      setReport(data);
    } catch (e) {
      toast.error('Sweep report failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const Section = ({ title, color, rows, testId }) => (
    <div className="bg-[#050003] border border-[#2A0F3D] p-3" data-testid={testId}>
      <p className="font-mono text-xs font-bold uppercase mb-2" style={{ color }}>{title}</p>
      <ul className="space-y-1">
        {rows.map(({ label, value, warn }) => (
          <li key={label} className="flex items-center justify-between font-mono text-xs">
            <span className="text-[#6B8E73]">{label}</span>
            <span className={warn ? 'text-[#FF9F0A] font-bold' : 'text-[#A3C7A8]'}>{value}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="bg-[#0C0612] border border-[#00F0FF] mb-6" data-testid="sweep-report-panel">
      <div className="flex items-center gap-3 px-5 py-3 border-b border-[#00F0FF] text-[#00F0FF]">
        <Database className="w-5 h-5" />
        <h2 className="font-mono text-sm font-bold uppercase tracking-wider">PRE-PILOT SWEEP · READ-ONLY DB AUDIT</h2>
      </div>

      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <p className="text-[#6B8E73] font-mono text-xs flex-1 min-w-[220px]">
            // Snapshot of schema health, photo storage state, orphan rows, leftover test data, and stuck records.
            Nothing here mutates the DB — use the cleanup tools below to act on findings.
          </p>
          <Button
            onClick={load}
            disabled={loading}
            className="bg-[#00F0FF] text-[#050003] hover:bg-[#33F4FF] rounded-none font-mono"
            data-testid="sweep-report-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'LOADING...' : (report ? 'REFRESH REPORT' : 'RUN SWEEP REPORT')}
          </Button>
        </div>

        {report && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="sweep-report-grid">
            <Section
              title="SCHEMA HEALTH"
              color="#39FF14"
              testId="sweep-schema"
              rows={[
                { label: 'products with region_stock=null', value: report.schema.products_region_stock_null, warn: report.schema.products_region_stock_null > 0 },
                { label: 'products with variant_stock=null', value: report.schema.products_variant_stock_null, warn: report.schema.products_variant_stock_null > 0 },
                { label: 'users missing region', value: report.schema.users_no_region, warn: report.schema.users_no_region > 0 },
              ]}
            />
            <Section
              title="PHOTO STORAGE"
              color="#FF3D8E"
              testId="sweep-photos"
              rows={[
                { label: 'total products', value: report.photos.total_products },
                { label: 'missing photo', value: report.photos.missing_photo, warn: report.photos.missing_photo > 0 },
                { label: 'legacy URL (wipes on restart)', value: report.photos.legacy_url_format, warn: report.photos.legacy_url_format > 0 },
                { label: 'persistent format', value: report.photos.persistent_format },
                { label: 'external host', value: report.photos.external_host },
              ]}
            />
            <Section
              title="ORPHAN PUSH SUBS"
              color="#FF9F0A"
              testId="sweep-orphans"
              rows={[
                { label: 'orphaned (user deleted)', value: report.orphans.push_subs_orphaned, warn: report.orphans.push_subs_orphaned > 0 },
                { label: 'total push subscriptions', value: report.orphans.push_subs_total },
              ]}
            />
            <Section
              title="TEST DATA LEFTOVERS"
              color="#BF5AF2"
              testId="sweep-test-data"
              rows={[
                { label: 'test-pattern users', value: report.test_data.test_user_count, warn: report.test_data.test_user_count > 0 },
                { label: 'test-pattern products', value: report.test_data.test_product_count, warn: report.test_data.test_product_count > 0 },
              ]}
            />
            <Section
              title="STUCK RECORDS"
              color="#FFD60A"
              testId="sweep-stuck"
              rows={[
                { label: 'non-terminal orders >7d old', value: report.stuck.non_terminal_orders_older_than_7d, warn: report.stuck.non_terminal_orders_older_than_7d > 0 },
                { label: 'overdue scheduled broadcasts', value: report.stuck.scheduled_broadcasts_overdue, warn: report.stuck.scheduled_broadcasts_overdue > 0 },
              ]}
            />
            <Section
              title="LIVE COUNTS"
              color="#00F0FF"
              testId="sweep-counts"
              rows={[
                { label: 'users (all roles)', value: report.counts.users },
                { label: 'active customers', value: report.counts.active_customers },
                { label: 'drivers', value: report.counts.drivers },
                { label: 'dispatchers / super-admin', value: `${report.counts.dispatchers} / ${report.counts.super_admins}` },
                { label: 'products', value: report.counts.products },
                { label: 'orders (lifetime)', value: report.counts.orders },
              ]}
            />

            {report.test_data.test_user_samples.length > 0 && (
              <div className="md:col-span-2 bg-[#050003] border border-[#BF5AF2] p-3" data-testid="sweep-test-samples">
                <p className="text-[#BF5AF2] font-mono text-xs font-bold uppercase mb-2">// SAMPLE TEST USERS (first 5)</p>
                <ul className="space-y-0.5 text-[#6B8E73] font-mono text-xs">
                  {report.test_data.test_user_samples.map((u, i) => (
                    <li key={i}>– {u.email} <span className="text-[#4B6B52]">[{u.role} · {u.region || 'no region'}]</span></li>
                  ))}
                </ul>
                <p className="text-[#A3C7A8] font-mono text-xs mt-3 pt-2 border-t border-[#2A0F3D]">
                  // ↓ Use <span className="text-[#FF9F0A]">CLEANUP TEST USERS</span> below to safely purge these accounts + cascading records.
                </p>
              </div>
            )}

            <p className="md:col-span-2 text-[#4B6B52] font-mono text-xs text-right" data-testid="sweep-generated-at">
              // generated_at: {new Date(report.generated_at).toLocaleString()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// OpsToolsPanel — super-admin pilot ops widgets
//   * Cleanup Test Users — preview + confirm + execute
// ============================================================================

function OpsToolsPanel() {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/cleanup-test-users/preview`,
        { withCredentials: true }
      );
      setPreview(data);
      setShowConfirm(true);
    } catch (e) {
      toast.error('Preview failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingPreview(false);
    }
  };

  const execute = async () => {
    setExecuting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/cleanup-test-users`,
        {},
        { withCredentials: true }
      );
      if (data.executed) {
        const { users, points_ledger, staff_points_ledger, user_audit_log, orders } = data.removed;
        toast.success(`Cleaned ${users} users + ${points_ledger + staff_points_ledger + user_audit_log + orders} cascaded docs`);
        setLastResult(data.removed);
      } else {
        toast.info(data.message || 'Nothing to clean');
        setLastResult({ users: 0, points_ledger: 0, staff_points_ledger: 0, user_audit_log: 0, orders: 0 });
      }
      setShowConfirm(false);
      setPreview(null);
    } catch (e) {
      toast.error('Cleanup failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setExecuting(false);
    }
  };

  const totalCascade = preview ? Object.values(preview.cascades || {}).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="bg-[#0C0612] border border-[#FF9F0A] mb-6" data-testid="ops-tools-panel">
      <div className="flex items-center gap-3 px-5 py-3 border-b border-[#FF9F0A] text-[#FF9F0A]">
        <AlertTriangle className="w-5 h-5" />
        <h2 className="font-mono text-sm font-bold uppercase tracking-wider">OPS TOOLS · SUPER-ADMIN</h2>
      </div>

      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <p className="text-[#39FF14] font-mono text-sm font-bold flex items-center gap-2">
              <Trash2 className="w-4 h-4" /> CLEANUP TEST USERS
            </p>
            <p className="text-[#6B8E73] font-mono text-xs mt-1">
              // Removes test-suite accounts (matched by email pattern) + cascading ledger / orders / audit entries.
              Real users are never touched. Run before reviewing dashboards or sharing the app for the pilot.
            </p>
          </div>
          <Button
            onClick={loadPreview}
            disabled={loadingPreview || executing}
            className="bg-[#FF9F0A] text-[#050003] hover:bg-[#FFAF2A] rounded-none font-mono"
            data-testid="cleanup-preview-btn"
          >
            {loadingPreview ? 'LOADING...' : 'PREVIEW CLEANUP'}
          </Button>
        </div>

        {lastResult && !showConfirm && (
          <div className="bg-[rgba(57,255,20,0.06)] border border-[#39FF14] p-3 text-[#A3C7A8] font-mono text-xs" data-testid="cleanup-last-result">
            // Last run: removed {lastResult.users} users · {lastResult.points_ledger} points entries · {lastResult.staff_points_ledger} staff entries · {lastResult.orders} orders · {lastResult.user_audit_log} audit rows
          </div>
        )}

        {showConfirm && preview && (
          <div className="bg-[#050003] border border-[#FF9F0A] p-4 space-y-3" data-testid="cleanup-confirm-modal">
            <p className="text-[#FF9F0A] font-mono text-sm font-bold">
              // CONFIRMATION REQUIRED — {preview.test_user_count} test users out of {preview.total_user_count} total
            </p>
            {preview.test_user_count === 0 ? (
              <p className="text-[#6B8E73] font-mono text-xs">// Nothing to clean. Dashboard is already pristine.</p>
            ) : (
              <>
                <div className="text-[#A3C7A8] font-mono text-xs space-y-0.5">
                  <p>// Sample emails:</p>
                  <ul className="ml-3 text-[#6B8E73]">
                    {preview.sample_emails.map(e => <li key={e}>– {e}</li>)}
                  </ul>
                </div>
                <div className="text-[#A3C7A8] font-mono text-xs space-y-0.5 border-t border-[#2A0F3D] pt-3">
                  <p>// Will cascade-delete:</p>
                  <ul className="ml-3 text-[#6B8E73]">
                    <li>– {preview.cascades.points_ledger_user_id} customer points entries</li>
                    <li>– {preview.cascades.points_ledger_related} related points entries</li>
                    <li>– {preview.cascades.staff_points_ledger_related} staff bonus entries</li>
                    <li>– {preview.cascades.user_audit_log} user audit entries</li>
                    <li>– {preview.cascades.orders} orders</li>
                    <li>– {preview.test_user_count} user accounts</li>
                  </ul>
                  <p className="mt-2 text-[#FF9F0A]">// Total cascade: {totalCascade + preview.test_user_count} document(s)</p>
                </div>
              </>
            )}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => { setShowConfirm(false); setPreview(null); }}
                disabled={executing}
                variant="outline"
                className="border-[#2A0F3D] text-[#6B8E73] hover:border-[#39FF14] hover:text-[#39FF14] rounded-none font-mono"
                data-testid="cleanup-cancel-btn"
              >
                CANCEL
              </Button>
              {preview.test_user_count > 0 && (
                <Button
                  onClick={execute}
                  disabled={executing}
                  className="bg-[#FF3B30] text-white hover:bg-[#FF5040] rounded-none font-mono"
                  data-testid="cleanup-confirm-btn"
                >
                  {executing ? 'CLEANING...' : 'CONFIRM & DELETE'}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Phase 7.0-lite — three sweep-driven cleanup actions */}
        <CleanupAction
          testid="cleanup-orphan-push"
          icon={BellOff}
          color="#00F0FF"
          title="PURGE ORPHAN PUSH SUBS"
          description="// Push subscriptions whose user no longer exists. Each one is a dead row the fanout loop keeps retrying. Safe to delete anytime."
          buttonLabel="PURGE"
          endpoint="/api/admin/cleanup/orphan-push-subs"
          successFormat={(d) => `Deleted ${d.deleted} orphan push subscription(s)`}
        />
        <ChunkedBatchAction
          testid="cleanup-legacy-photos"
          icon={Wand2}
          color="#FF3D8E"
          title="REGENERATE LEGACY PHOTOS"
          description="// Products on the pre-Phase-5.6 URL format. These vanish on the next pod restart. Runs ONE at a time (~10-15s each) so the proxy never times out. May cost a few cents per product."
          endpoint="/api/admin/cleanup/regenerate-legacy-photos/one"
          actionLabel="REGENERATE"
        />
        <ChunkedBatchAction
          testid="cleanup-missing-photos"
          icon={ImageIcon}
          color="#39FF14"
          title="FILL MISSING PHOTOS"
          description="// Products with no image_url. Runs AI generation for each. ONE at a time so large batches never time out."
          endpoint="/api/products/generate-missing-images/one"
          actionLabel="GENERATE"
        />
      </div>
    </div>
  );
}


// ============================================================================
// CleanupAction — small reusable widget for one-shot ops triggers (no looping)
// For long-running batches, use ChunkedBatchAction (imported above).
// ============================================================================

function CleanupAction({ testid, icon: Icon, color, title, description, buttonLabel, endpoint, successFormat }) {
  const [running, setRunning] = useState(false);
  const [last, setLast] = useState(null);

  const run = async () => {
    setRunning(true);
    setLast(null);
    try {
      const { data } = await axios.post(
        `${API_URL}${endpoint}`,
        {},
        { withCredentials: true, timeout: 120_000 }
      );
      const msg = successFormat(data);
      toast.success(msg);
      setLast(msg);
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="border-t border-[#2A0F3D] pt-4" data-testid={testid}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <p className="font-mono text-sm font-bold flex items-center gap-2" style={{ color }}>
            <Icon className="w-4 h-4" /> {title}
          </p>
          <p className="text-[#6B8E73] font-mono text-xs mt-1">{description}</p>
        </div>
        <Button
          onClick={run}
          disabled={running}
          className="rounded-none font-mono"
          style={{ background: color, color: '#050003' }}
          data-testid={`${testid}-btn`}
        >
          {running ? 'WORKING...' : buttonLabel}
        </Button>
      </div>
      {last && (
        <div className="mt-2 bg-[rgba(57,255,20,0.06)] border border-[#39FF14] p-2 text-[#A3C7A8] font-mono text-xs" data-testid={`${testid}-result`}>
          // {last}
        </div>
      )}
    </div>
  );
}


// ============================================================================
// LegacyPhotoCleanupAction (DEPRECATED — see /components/ChunkedBatchAction.js)
// Removed: refactored into the shared ChunkedBatchAction component so the
// chunked loop pattern can be reused for any future long-running batch op
// (broadcast send to 1000 users, region migration, points recompute, etc).
// ============================================================================
